/**
 * Direct Debit page behaviours (only bound when the DD form is present):
 *   1. Sort-code focus hopping — jump to the next 2-digit box as each fills.
 *   2. "Find bank address" — validate the sort code + account number against
 *      the bank service (GET /enter/validate-bank/{account}/{dashed-sort}) and,
 *      on success, fill the bank address fields; on failure show inline errors.
 *
 * The endpoint returns the raw upstream JSON wrapped as a JSON string, so the
 * payload is parsed with JSON.parse ($.parseJSON was removed in jQuery 4).
 * When bank validation isn't configured server-side the payload is empty and
 * the lookup simply reports that details couldn't be validated.
 */
import $ from 'jquery';

interface BankData {
    IsCorrect?: string;
    IsDirectDebitCapable?: string;
    Bank?: string;
    ContactAddressLine1?: string;
    ContactAddressLine2?: string;
    ContactPostTown?: string;
    ContactPostcode?: string;
}

export default function initDirectDebit(): void {
    const $lookup = $('#look_up_address');

    if (!$lookup.length) {
        return; // not the Direct Debit page
    }

    // User-facing copy comes from data-msg-* attributes on the lookup button
    // (server-rendered from Site Values so it can be translated); the English
    // literals are the fallback if an attribute is ever missing.
    const ds = ($lookup[0] as HTMLElement).dataset;
    const messages = {
        invalid: ds.msgBankDetailsInvalid
            || 'Sorry but your account number or sort code are invalid. Please try again.',
        accountRequired: ds.msgAccountNumberRequired || 'Account number is required.',
        sortRequired: ds.msgSortCodeRequired || 'Sort code is required.',
        validationUnavailable: ds.msgBankValidationUnavailable
            || 'Sorry, we could not validate your bank details. Please enter your address manually.',
    };

    // 1. Sort-code focus hopping.
    const hop = (fromId: string, toId: string): void => {
        $('#' + fromId).on('input', function () {
            const max = parseInt($(this).attr('maxlength') || '0', 10);
            if (max > 0 && (($(this).val() as string) || '').length >= max) {
                $('#' + toId).trigger('focus');
            }
        });
    };
    hop('sort_code_1', 'sort_code_2');
    hop('sort_code_2', 'sort_code_3');

    // 2. Find bank address (the button is type=submit, so stop the submit).
    $lookup.on('click', function (event) {
        event.preventDefault();
        $('.accountNumberError').html('');
        $('.sortCodeError').html('');

        const accountNumber = ($('#account_number').val() as string) || '';
        const s1 = ($('#sort_code_1').val() as string) || '';
        const s2 = ($('#sort_code_2').val() as string) || '';
        const s3 = ($('#sort_code_3').val() as string) || '';
        const sortValid = s1.length === 2 && s2.length === 2 && s3.length === 2;

        if (!accountNumber || !sortValid) {
            if (!accountNumber) $('.accountNumberError').html(messages.accountRequired);
            if (!sortValid) $('.sortCodeError').html(messages.sortRequired);
            return;
        }

        $.getJSON(`/enter/validate-bank/${accountNumber}/${s1}-${s2}-${s3}`)
            .done((data: unknown) => {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                const bankData: BankData | undefined = Array.isArray(parsed) ? parsed[0] : undefined;

                if (!bankData || bankData.IsCorrect !== 'True' || bankData.IsDirectDebitCapable !== 'True') {
                    $('.accountNumberError').html(messages.invalid);
                    $('.sortCodeError').html(messages.invalid);
                    return;
                }

                // Reveal the manual address block and populate it.
                $('#toggle-address-fields-visibility').prop('checked', true);
                $('#bank_address_1').val(bankData.Bank ?? '');
                if (bankData.ContactAddressLine1) {
                    $('#bank_address_2').val(bankData.ContactAddressLine1);
                    $('#bank_address_3').val(bankData.ContactAddressLine2 ?? '');
                } else if (bankData.ContactAddressLine2) {
                    $('#bank_address_2').val(bankData.ContactAddressLine2);
                }
                $('#bank_town').val(bankData.ContactPostTown ?? '');
                $('#bank_postcode').val(bankData.ContactPostcode ?? '');
                $('#address-manual').slideDown();
            })
            .fail(() => {
                $('.accountNumberError').html(messages.validationUnavailable);
            });
    });
}
